import { AuthErrorKind } from '@griply/types';
import Typography from '@mui/material/Typography';
import { useResetLink, useResetPassword } from 'features/user';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthPageLayout } from 'shared/components/ui/auth-page-layout';
import { PageLoader } from 'shared/components/ui/page-loader';
import { ResetPasswordForm } from 'shared/components/ui/reset-password-form';
import { QueryParamOptions, useQueryParam } from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';

import * as Styled from './reset-password.style';

const InvalidLinkFallback = () => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="h1" textAlign="center">
        {t('forms.reset-password.invalid-link.title')}
      </Typography>

      <Styled.Content>
        <Typography variant="body2" textAlign="center">
          {t('forms.reset-password.invalid-link.description')}
        </Typography>
      </Styled.Content>
    </>
  );
};

const ResetPasswordContent: React.FC = () => {
  const { t } = useTranslation();
  const [code] = useQueryParam(QueryParamOptions.OobCode);
  const { email, isDeadLink } = useResetLink(code);
  const { resetPassword, isLoading, error } = useResetPassword();

  if (!code || isDeadLink || error === AuthErrorKind.InvalidLink) {
    return <InvalidLinkFallback />;
  }

  const submitForm = ({ password }: { password: string }) =>
    resetPassword({ password, code });

  return (
    <>
      <Typography variant="h1" textAlign="center">
        {t('forms.reset-password.title')}
      </Typography>

      <Styled.Content>
        <Typography variant="body2">
          {t('forms.reset-password.description')}
        </Typography>
        <Typography variant="body2">
          {t('forms.reset-password.warning')}
        </Typography>

        <ResetPasswordForm
          email={email}
          onSubmit={submitForm}
          isLoading={isLoading}
          error={error ?? undefined}
        />
      </Styled.Content>
    </>
  );
};

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation();

  return (
    <AuthPageLayout>
      <Suspense fallback={<PageLoader />}>
        <ResetPasswordContent />
      </Suspense>

      <Styled.Anchor to={Paths.Login}>
        {t('forms.reset-password.back-to-login.label')}
      </Styled.Anchor>
    </AuthPageLayout>
  );
};
